import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { fDateTime } from 'src/utils/format-time';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import CasButton from 'src/compass/shared/components/CasButton/CasButton';

import CasBaseDialog from '../CasDialog/CasBaseDialog';
import {
  IPendingBatch,
  IPendingEmail,
  IPendingHold,
  PendingEmailStatus,
  PENDING_EMAILS_QUERY_KEY,
  STALE_AFTER_SECONDS,
  channelNoun,
  isNotDelivered,
} from './usePendingEmails';

interface Props {
  pending: { messages: IPendingEmail[]; batches: IPendingBatch[]; holds: IPendingHold[] };
  onClose: VoidFunction;
}

/*
  Derived from the server-computed age, NOT from dateTime.

  That timestamp is stamped with DateTime.Now and serialized without a timezone (see
  MsgHistoryPendingResponseBo), so a row written by an Azure app service - which runs UTC - arrives
  as UTC digits that the browser then reads as local: a 5:26 PM send displayed as 10:26 PM.
  AgeSeconds is subtracted on the server precisely so it carries no timezone, and anchoring it to the
  browser's own clock gives the sender's local time in every environment.
*/
const whenRequested = (ageSeconds: number) => fDateTime(new Date(Date.now() - ageSeconds * 1000));

// "in 4 minutes", rounded up - "in 0 minutes" would read as stuck.
const retryIn = (seconds: number) => {
  if (seconds < 60) return 'in under a minute';

  const minutes = Math.ceil(seconds / 60);
  return `in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

// When it was requested, highlighted when it is taking longer than it should.
function RequestedAt({ ageSeconds, isStale }: { ageSeconds: number; isStale: boolean }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      sx={isStale ? { bgcolor: 'warning.lighter', px: 0.75, py: 0.25, borderRadius: 0.5 } : undefined}
    >
      {whenRequested(ageSeconds)}
    </Typography>
  );
}

/*
  A message's state in a line: why it failed, when it will be tried again, or how it is getting on.
*/
function MessageState({ row }: { row: IPendingEmail }) {
  if (row.status === PendingEmailStatus.Failed) {
    return (
      <Alert severity="error" sx={{ flexGrow: 1, py: 0 }}>
        {row.errorMessage || 'The message could not be sent.'}
      </Alert>
    );
  }

  // Sent, but RingCentral reported that it did not arrive - at all, or at some of a group text's
  // numbers (story 15724).
  if (row.status === PendingEmailStatus.Undeliverable) {
    return (
      <Alert severity="error" sx={{ flexGrow: 1, py: 0 }}>
        {row.errorMessage || 'The text was sent, but did not arrive.'}
      </Alert>
    );
  }

  if (row.status === PendingEmailStatus.Delivered) {
    return (
      <Alert severity="warning" sx={{ flexGrow: 1, py: 0 }}>
        {row.errorMessage}
      </Alert>
    );
  }

  // Failed for a reason that passes; the send queue tries it again by itself (story 15723).
  if (row.retryInSeconds != null) {
    return (
      <Alert severity="warning" sx={{ flexGrow: 1, py: 0 }}>
        Trying again {retryIn(row.retryInSeconds)}.{row.errorMessage ? ` ${row.errorMessage}` : ''}
      </Alert>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
      {row.waiting ? 'Waiting its turn…' : 'Sending…'}
    </Typography>
  );
}

/*
  The sender's held messages on one channel, said once: why, how many, and the two ways out. Resume
  puts them back in the queue (connecting RingCentral does that for texts by itself); Cancel the rest
  drops them for good, so it asks first - inline, as a confirmation dialog would open underneath
  this one.
*/
function HoldLine({ hold, onDone }: { hold: IPendingHold; onDone: VoidFunction }) {
  const [confirming, setConfirming] = useState(false);

  const { mutate: resume, isPending: resuming } = useUpdateMutation({
    url: 'Msg/History/Hold/Resume',
    showLoader: false,
    onSuccess: onDone,
  });

  const { mutate: cancel, isPending: canceling } = useUpdateMutation({
    url: 'Msg/History/Hold/Cancel',
    showLoader: false,
    onSuccess: onDone,
  });

  const noun = channelNoun(hold.channel, hold.count);

  return (
    <Alert severity="warning">
      <Typography variant="body2">{hold.reason}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {hold.count} {noun} on hold
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
        {confirming ? (
          <>
            <Typography variant="body2">
              Cancel {hold.count} {noun}? {hold.count === 1 ? 'It' : 'They'} will not be sent.
            </Typography>
            <Button
              size="small"
              color="error"
              variant="contained"
              disabled={canceling}
              onClick={() => cancel({ channel: hold.channel })}
            >
              Yes, cancel them
            </Button>
            <Button size="small" color="inherit" onClick={() => setConfirming(false)}>
              No
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="contained"
              disabled={resuming}
              onClick={() => resume({ channel: hold.channel })}
            >
              Resume
            </Button>
            <Button size="small" color="error" onClick={() => setConfirming(true)}>
              Cancel the rest
            </Button>
          </>
        )}
      </Stack>
    </Alert>
  );
}

// "212 of 300 sent, 3 failed, 85 waiting (2 being retried), 5 on hold".
const batchCounts = (batch: IPendingBatch) => {
  const parts = [`${batch.sentCount} of ${batch.total} sent`];

  if (batch.undeliverableCount) parts.push(`${batch.undeliverableCount} not delivered`);
  if (batch.failedCount) parts.push(`${batch.failedCount} failed`);
  if (batch.waitingCount) {
    parts.push(
      `${batch.waitingCount} waiting${batch.retryingCount ? ` (${batch.retryingCount} being retried)` : ''}`
    );
  }
  if (batch.heldCount) parts.push(`${batch.heldCount} on hold`);
  if (batch.canceledCount) parts.push(`${batch.canceledCount} canceled`);

  return parts.join(', ');
};

/*
  A Batch Send as one line (story 15723) - it used to be a line per message, mostly "Waiting its
  turn". Its failed messages open underneath, each with why; Dismiss clears the whole batch.
*/
function BatchLine({ batch, onDismiss }: { batch: IPendingBatch; onDismiss: VoidFunction }) {
  const [showFailed, setShowFailed] = useState(false);

  const settled = batch.sentCount + batch.undeliverableCount + batch.failedCount + batch.canceledCount;
  const onlyUndelivered = batch.failed.every(isNotDelivered);
  const isStale = batch.sendingSeconds != null && batch.sendingSeconds > STALE_AFTER_SECONDS;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" noWrap>
          {batch.subject}
        </Typography>
        <RequestedAt ageSeconds={batch.ageSeconds} isStale={isStale} />
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Batch Send of {batch.total} {channelNoun(batch.channel, batch.total)}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={batch.total ? (settled * 100) / batch.total : 0}
        color={batch.failedCount || batch.undeliverableCount || batch.heldCount ? 'error' : 'primary'}
        sx={{ my: 1 }}
      />

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {batchCounts(batch)}
          {batch.failed.length > 0 && (
            <>
              {' - '}
              <Link component="button" type="button" variant="body2" onClick={() => setShowFailed((v) => !v)}>
                {showFailed ? 'Hide' : 'Show'} {onlyUndelivered ? 'not delivered' : 'failed'}
              </Link>
            </>
          )}
        </Typography>

        <Button size="small" color="inherit" onClick={onDismiss}>
          Dismiss
        </Button>
      </Stack>

      <Collapse in={showFailed} unmountOnExit>
        <Stack spacing={1} sx={{ mt: 1, pl: 2 }}>
          {batch.failed.map((row) => (
            <Box key={row.id}>
              <Typography variant="body2" color="text.secondary">
                To: {row.to.join(', ')}
              </Typography>
              <MessageState row={row} />
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function CasPendingEmailsDialog({ pending, onClose }: Props) {
  const queryClient = useQueryClient();
  const { messages, batches, holds } = pending;

  const refresh = () => queryClient.invalidateQueries({ queryKey: [PENDING_EMAILS_QUERY_KEY] });

  const { mutate } = useUpdateMutation({
    url: 'Msg/History/Acknowledge',
    // No backdrop loader: dismissing is instant and the loader drives the global dialog slot.
    showLoader: false,
    // Only invalidate. Closing is left to the pill, which drops this dialog once nothing is
    // pending - so dismissing one of several rows leaves the dialog open on the rest.
    onSuccess: refresh,
  });

  const dismiss = ({ ids = [], batchIds = [] }: { ids?: number[]; batchIds?: number[] }) =>
    mutate({ ids, batchIds });

  const hasError =
    messages.some((row) => row.status === PendingEmailStatus.Failed) ||
    batches.some((batch) => batch.failed.some((row) => row.status === PendingEmailStatus.Failed));

  const hasNotDelivered =
    messages.some(isNotDelivered) || batches.some((batch) => batch.failed.some(isNotDelivered));

  const title = (() => {
    if (holds.length) return 'Messages on hold';
    if (hasError) return 'Messages not sent';
    if (hasNotDelivered) return 'Texts not delivered';
    return 'Messages in progress';
  })();

  return (
    <CasBaseDialog
      open
      showCloseIcon
      maxWidth="sm"
      title={title}
      onClose={onClose}
      // Above the pill (modal + 50), so the pill doesn't cover this dialog's own corner.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 60 }}
      actions={
        <>
          <CasSecondaryButton onClick={onClose}>Close</CasSecondaryButton>
          {/* "Dismiss", never "Cancel" - this only clears the indicator, it does not stop a send
              that is already on its way. Labelled "all" because it clears every line listed,
              in-flight ones included. A hold stays: only Resume or Cancel settles it. */}
          {(messages.length > 0 || batches.length > 0) && (
            <CasButton
              onClick={() =>
                dismiss({
                  ids: messages.map((row) => row.id),
                  batchIds: batches.map((batch) => batch.batchId),
                })
              }
            >
              Dismiss all
            </CasButton>
          )}
        </>
      }
    >
      <Stack divider={<Divider flexItem />} spacing={2}>
        {holds.map((hold) => (
          <HoldLine key={hold.channel} hold={hold} onDone={refresh} />
        ))}

        {batches.map((batch) => (
          <BatchLine
            key={`batch-${batch.batchId}`}
            batch={batch}
            onDismiss={() => dismiss({ batchIds: [batch.batchId] })}
          />
        ))}

        {messages.map((row) => {
          /*
            Flagged when the message that should be going out is taking longer than it should: its
            own send has run long, or a restart left it (the send queue picks it up again after one,
            see MsgSendService), or it has not started a minute after it was queued. Timed from the
            start of its own send once there is one, not from when it was queued - a batch's later
            messages are queued minutes before their turn comes. Never a message waiting behind
            another of the sender's, nor one waiting to be tried again.
          */
          const isStale =
            row.status === PendingEmailStatus.Requested &&
            !row.waiting &&
            row.retryInSeconds == null &&
            (row.sendingSeconds ?? row.ageSeconds) > STALE_AFTER_SECONDS;

          return (
            <Box key={row.id}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" noWrap>
                  {row.subject}
                </Typography>
                <RequestedAt ageSeconds={row.ageSeconds} isStale={isStale} />
              </Stack>

              <Typography variant="body2" color="text.secondary">
                To: {row.to.join(', ')}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <MessageState row={row} />

                <Button size="small" color="inherit" onClick={() => dismiss({ ids: [row.id] })}>
                  Dismiss
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </CasBaseDialog>
  );
}
